import { useEffect, useState, useRef, useMemo, useCallback } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { green } from "@mui/material/colors";
import { useDispatch } from "react-redux";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { withTranslation } from "react-i18next";
import {size, isEmpty, isEqual, isUndefined, isNull, find, includes} from "lodash";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import PaginationContainer from "@plannel/components/PaginationContainer"
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import SplitterContainer from "@plannel/components/SplitterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import colorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import ElasticSearchUtil from "@plannel/pages/utils/elastic-search-util";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import bfAccuracyService from "@plannel/services/dp/bf-accuracy-service";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import DpVersionSelect from "@plannel/pages/demand-plan/dp-workbench/DpVersionSelect";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";

const PERIOD_START = "periodStart";
const PERIOD_END = "periodEnd";
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const GAP_QTY = "gapQty";
const ACCURACY = "accuracy";

const BfAccuracyReport = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const MAX_END_DATE = new ZDate(new ZDate().setDate(new ZDate().getDate() + 1));
  const TODAY = new ZDate();
  const START_DATE = new ZDate(TODAY.getFullYear(), TODAY.getMonth() - 3, 1, 0, 0, 0);
  const timeDimensions = useRef(Object.values(codeUtil.buckets));
  const { colorSet, snackBarMessageColor } = colorUtil;
  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageSizes = [50, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? {});
  const [vListReload, setVListReload] = useState(false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [custHrchyDefaultValue] = useState(null);
  const [itemHrchyDefaultValue] = useState(null);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [startDate, setStartDate] = useState(reduxViewState?.startDate ?? START_DATE);
  const [endDate, setEndDate] = useState(reduxViewState?.endDate ?? new ZDate());
  const [maxDate, setMaxDate] = useState(MAX_END_DATE);
  const [timeDimension, setTimeDimension] = useState(reduxViewState?.timeDimension ?? timeDimensions.current[1]);
  const { MONTH } = codeUtil.buckets;
  const [planConfigObj, setPlanConfigObj] = useState({});
  const [weekHeader, setWeekHeader] = useState([]);
  const [originHeader, setOriginHeader] = useState([]);
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const { SA_QTY, POS_SA_QTY, BF_QTY } = codeUtil.dpMeasure;
  const [allMeasure, setAllMeasure] = useState(null);
  const [actualQtyName, setActualQtyName] = useState(SA_QTY.name);
  const allMeasureSetRef = useRef(false);
  const [measures, setMeasures] = useState([]);
  const [displayNames, setDisplayNames] = useState({});
  const [formatHeaders, setFormatHeaders] = useState([]);
  const exportColumnDefs = useRef();
  const EXCEL_EXPORT = "excelExport";

  const reduxState = reduxUtil.createObjectState(viewName);
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [chartData, setChartData] = useState([]);
  const measureValCellRenderer = ({ value }) => {
    return displayNames?.[value] || GridUtils.gridValueL10N(value);
  };


  const chartCellRenderer = (value) => {
    return displayNames?.[value] || null;
  };

  const chartSeries = useMemo(() => [
    {
      field: chartCellRenderer(actualQtyName) ?? actualQtyName,
      text: chartCellRenderer(actualQtyName) ?? t(actualQtyName),
      type: codeUtil.toggleType.AREA,
      typeOptions: { fill: colorSet(0) },
      orientation: "left",
      groupName: t("measure"),
    },
    {
      field: GAP_QTY,
      text: t(GAP_QTY),
      type: codeUtil.toggleType.BAR,
      typeOptions: { fill: colorSet(1) },
      orientation: "left",
      groupName: t("measure"),
    },
    {
      field: chartCellRenderer(BF_QTY.name) ?? BF_QTY.name,
      text: chartCellRenderer(BF_QTY.name) ?? t(BF_QTY.name),
      type: codeUtil.toggleType.LINE,
      typeOptions: { fill: colorSet(2) },
      orientation: "left",
      groupName: t("measure"),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [displayNames, t, ]);

  const gridRef = useRef();
  const [gridData, setGridData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [reload, setReload] = useState(false);
  const [dateLoad, setDateLoad] = useState(false);
  const [loading, setLoading] = useState(false);

  const onGridReady = () => {
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true  });
  };

  const getDisplayName = (measures) => {
    const names = [];
     for (const measure of measures) {
       names.push(chartCellRenderer(measure) ?? measure);
     }
     return names;
  }

  const setColumnDefsByOrder = () => {
    return [
      {
        headerName: "customerCd",
        showRowGroup: "customerCd",
        colId: "custCdId",
        pinned: "left", lockPinned: true,
        width: 150,
        cellRenderer: "agGroupCellRenderer",
        type: ["nonEditableColumn"],
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_CdId") ? sortParams?.sortType : null,
      },
      {
        headerName: "customerName",
        showRowGroup: "customerNm",
        colId: "cust_levelId",
        chartDataType: "category",
        width: 160,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
      },
      {
        headerName: "itemCd",
        showRowGroup: "itemCd",
        colId: "itemCdId",
        chartDataType: "category",
        width: 220,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "prod_CdId") ? sortParams?.sortType : null,
      },
      {
        headerName: "itemName",
        showRowGroup: "itemNm",
        colId: "productlevelId",
        chartDataType: "category",
        width: 220,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,
      },
      {
        headerName: "measure",
        field: "measure",
        width: 210,
        type: ["nonEditableColumn"],
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        valueFormatter: measureValCellRenderer,
        headerComponent: CustomHeaderMenuIcon,
        columnGroupShow: "closed",
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen,
        },
      },
      { headerName: "customerCd", field: "customerCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_CdId") ? sortParams?.sortType : null,},
      { headerName: "customerName", field: "customerNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,},
      { headerName: "itemCd", field: "itemCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "prod_CdId") ? sortParams?.sortType : null,},
      { headerName: "itemNm", field: "itemNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,},
    ];
  };

  const getExportDefaultColumnDefs = () => {
    return [
      { field:  "customerCd", colId: "custCdGrp", rowGroup: true, hide: true},
      { field:  "customerNm", colId: "custNmGrp", rowGroup: true, hide: true},
      { field:  "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true},
      { field:  "itemNm", colId: "itemNmGrp", rowGroup: true, hide: true},
      { headerName: "customerCd", field: "customerCd",  cellClass: 'stringType' },
      { headerName: "customerName", field: "customerNm" },
      { headerName: "itemCd", field: "itemCd",  cellClass: 'stringType' },
      { headerName: "itemName", field: "itemNm" },
      { headerName: "measure", field: "measure", valueFormatter: measureValCellRenderer},
    ];
  };


  const getSearchFormat = (date, target) => {
    if (date && typeof date !== "string") {
      if (codeUtil.isWeekBucket(timeDimension)) {
        return new ZDate(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
      } else if (timeDimension === MONTH) {
        if (target === PERIOD_END) {
          return new ZDate(date.getFullYear(), date.getMonth()+1, 0, 0, 0, 0);
        }
        return new ZDate(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
      }
      return new ZDate(date.getFullYear(), 0, 1, 0, 0, 0);
    }
    return date;
  };

  const settingSortParams = (params) => {
    const columnState = params.api.getColumnState();
    let sortState = sortParams ?? {};

    const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
    if (isEqual(params.source, "uiColumnSorted")) {
      let sortColNm = sortColumn?.colId;
      if (includes(sortColumn?.colId, "item")) {
        sortColNm = sortColumn.colId.replace("item", "prod_");
      } else if (includes(sortColumn?.colId, "cust") && !includes(sortColumn?.colId, "_")) {
        sortColNm = sortColumn.colId.replace("cust", "cust_");
      }

      sortState = { orderByColumn: sortColNm, sortType: sortColumn?.sort };
    }
    setSortParams(sortState);
  }

  const getRequestParams = () => {
    const params = {
      startDate: dateUtils.formatDate(getSearchFormat(startDate, PERIOD_START), "yyyy-MM-dd"),
      endDate: dateUtils.formatDate(getSearchFormat(endDate, PERIOD_END), "yyyy-MM-dd"),
      custHrchyCds: (isEqual(customerSearch.current, "") && extraCustomerCodesRef.current?.length === 0) ? filterValue?.customerHrchys?.map(data => data?.hrchyCd) ?? null : null,
      itemHrchyCds: (isEqual(itemSearch.current, "") && extraItemCodesRef.current?.length === 0) ? filterValue?.category?.map(data => data?.hrchyCd) ?? null : null,
      customerFilter: extraCustomerCodesRef.current?.length > 0 ? [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean) : (customerSearch.current ?? ""),
      itemFilter: extraItemCodesRef.current?.length > 0 ? [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean) : (itemSearch.current ?? ""),
      planVersionId: selectVersion?.id ?? "",
      measures
    };

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return { ...params, ...sortParams };
  };

  const getStartDateByEndDate = (start, end) => {
    if (dateUtils.getDateWithoutTime(start) > dateUtils.getDateWithoutTime(end)) {
      return new ZDate(start.getFullYear(), start.getMonth() - 6, 1, 0, 0, 0);
    }
    return start;
  };

  const valueFormatter = (params, isExport = false) => {
    const { data, colDef } = params;
    if (isNull(data) || isUndefined(data)) {
      return "";
    }

    const { measure } = data;
    const fieldName = colDef?.field;
    const value = data?.[fieldName];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    if (!isEmpty(measure) && isEqual(measure, ACCURACY)) {
      if (isExport) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 3, minimumFractionDigits: 3 }).replace(/,/g, "");
      } else {
        return NumberUtil.formatNumber(value, { style: "percent", maximumFractionDigits: 1, minimumFractionDigits: 1 });
      }
    }

    if (isExport) {
      return NumberUtil.formatNumber(value, { maximumFractionDigits: 0, minimumFractionDigits: 0 }).replace(/,/g, "");
    }

    return NumberUtil.formatNumber(value, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  };
  valueFormatter.customType = EXCEL_EXPORT;

  const setGridHeaders = (headers, bucket) => {
    const formatHeaderList = [];
    const colDefs = setColumnDefsByOrder();
    const exportColDefs = getExportDefaultColumnDefs();

    const setHeaderCols = (headerName, field) => {
      colDefs.push({
        headerName,
        field,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + field,
        width: 110,
        type: ["numberColumn", "rightAligned"],
        chartDataType: "series",
        // cellRenderer: NumberValueFormatBlank,
        valueFormatter,
        editable: false,
      });

      exportColDefs.push({
        headerName,
        field,
        // colId: GridUtils.IGNORE_P13N_COLLID,
        width: 120,
        valueGetter: params => valueFormatter(params, true),
        cellClass: params => {
          const value = Number(params?.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      })
    };

    if (codeUtil.isWeekBucket(bucket)) {
      const sortHeaders = Object.keys(headers).sort();
      for (const sortHeader of sortHeaders) {
        formatHeaderList.push(headers[sortHeader]);
        setHeaderCols(headers[sortHeader], headers[sortHeader]);
      }
    } else {
      for (const header of headers) {
        const headerFormat = dateUtils.headerDateFormatter(new ZDate(header), bucket);
        formatHeaderList.push(headerFormat);
        setHeaderCols(headerFormat, headerFormat);
      }
    }
    exportColumnDefs.current = exportColDefs;
    return { formatHeaderList, colDefs };
  };

  const setGridDatas = (headers, results) => {
    const datas = [];
    const setMeasureNm = obj => {
      const name = obj.measure?.name;
      const isDisplay = displayNames && !isEmpty(displayNames[name]);
      return name ? isDisplay ? displayNames[name] : name : obj.measureCd;
    };

    const displayNamesByMeasure = [];
    for (const measure of measures) {
      displayNamesByMeasure.push(displayNames?.[measure] || measure);
    }

    if (results.data) {
      const measureOrder = size(displayNamesByMeasure) > 0 ? displayNamesByMeasure : [];
      const sortedResults = results.data.sort((a, b) => measureOrder.indexOf(setMeasureNm(a)) - measureOrder.indexOf(setMeasureNm(b)));
      for (const result of sortedResults) {
        if (!displayNamesByMeasure.includes(setMeasureNm(result))) {
          continue;
        }
        const row = { measure: setMeasureNm(result) };
        row.customerCd = result.customerHrchyCd ?? result.customerCd;
        row.customerNm = result.customerNm ?? result.customerHrchyNm;
        // row.itemHrchyNm = result.itemHrchyNm ?? result.itemNm;
        row.itemNm = result.itemNm ?? result.itemHrchyNm;
        row.itemCd = result.itemCd ?? result.itemHrchyCd;
        row.resultRow = result;
        row.currency = results.currency;

        for (let i = 0; i < headers.length; i++) {
          const header = headers[i];
          if (result.pivotData) {
            row[header] = result.pivotData[i];
          }
        }
        datas.push(row);
      }
    }
    return datas;
  };

  const handleLoadData = response => {
    if (!response.results) {
      setGridData([]);
      return;
    } else {
      const { header: headers, bucket } = response.resultData;
      let sortHeaders = headers;
      if (codeUtil.isWeekBucket(bucket)) {
        sortHeaders = Object.keys(headers).sort();
      }
      const gridHeaders = setGridHeaders(headers, bucket);
      const datas = setGridDatas(gridHeaders.formatHeaderList, response.resultData);
      setFormatHeaders(gridHeaders.formatHeaderList);
      setColumnDefs(gridHeaders.colDefs);
      setGridData(datas);
      setWeekHeader(headers);
      setOriginHeader(sortHeaders);
    }

    const maxDate = new ZDate(response.endDate);
    if (maxDate < new ZDate(endDate)) {
      setEndDate(maxDate);
      setStartDate(getStartDateByEndDate(startDate, maxDate));
    }
    // setEndMaxDate(maxDate);
    const pageResult = response?.resultData?.page;
    if (pageResult) {
      setGridPages(pageResult.totalPages);
    }
  };

  const setChart = (selectedRows) => {
    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    const actualName = chartCellRenderer(actualQtyName) ?? actualQtyName;
    const bfName = chartCellRenderer(BF_QTY.name) ?? BF_QTY.name;
    let confirm = {};
    let actual = {};
    let bfQty = {};
    for (const row of selectedRows) {
      if (row.measure === GAP_QTY) {
        confirm = row;
      } else if (row.measure === actualName) {
        actual = row;
      } else if (row.measure === bfName) {
        bfQty = row;
      }
    }

    const getFormatDate = index => {
      if (codeUtil.isWeekBucket(timeDimension)) {
        return weekHeader[originHeader[index]];
      } else {
        return dateUtils.headerDateFormatter(new ZDate(originHeader[index]), timeDimension);
      }
    };
    const chartDataList = [];
    for (let idx = 0; idx < originHeader.length; idx++) {
      const cData = {};
      if (Object.keys(confirm).length > 0) {
        cData[GAP_QTY] = confirm[formatHeaders[idx]];
      }
      if (Object.keys(actual).length > 0) {
        cData[actualName] = actual[formatHeaders[idx]];
      }
      if (Object.keys(bfQty).length > 0) {
        cData[bfName] = bfQty[formatHeaders[idx]];
      }
      cData.yyyymmdd = getFormatDate(idx);
      chartDataList.push(cData);
    }
    setChartData(chartDataList);
  };

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  };

  const initCheck = params => {
    if (gridData) {
      if (params.type === "componentStateChanged" && params?.rowData) {
        let firstLeafNode = null;
        params.api.forEachLeafNode(node => { if (!firstLeafNode) firstLeafNode = node; });
        if (firstLeafNode) {
          const targetNodes = firstLeafNode?.parent?.allLeafChildren ?? [];
          params.api.setNodesSelected({ nodes: targetNodes, newValue: true, source: CHECKBOX_SELECTED });
        }
      }
    }
  };

  const getRowStyle = params => {
    const measureOrder = size(measures) > 0 ? measures : SA_QTY.name;
    if (params?.data?.measure && isEqual(params.data.measure, measureOrder[0])) {
      return { background: green[50] };
    }
    return null;
  };

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);

  };

  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, []);

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, []);

  // z_measure(measureService.getAll())의 실제 표시명(name)을 measureCd 기준으로 codeUtil.dpMeasure의
  // 식별자(.name)에 매핑한다. allMeasure/measures/grid의 measure 필드는 이 식별자를 그대로 써야 하므로
  // displayNames는 별도 state로만 두고 allMeasure에는 절대 반영하지 않는다.
  const retrieveMeasureDisplayName = () => {
    measureService.getAll({isBasic: true})
      .then(response => {
        const results = response?.data?.results;
        if (isEmpty(results)) {
          return;
        }
        const nameByMeasureCd = {};
        for (const measure of results) {
          nameByMeasureCd[measure.measureCd] = measure.name;
        }

        const nextDisplayNames = {};
        for (const measure of Object.values(codeUtil.dpMeasure)) {
          const zMeasureName = nameByMeasureCd[measure.cd];
          if (zMeasureName && !isEqual(zMeasureName, measure.name)) {
            nextDisplayNames[measure.name] = zMeasureName;
          }
        }
        setDisplayNames(nextDisplayNames);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrievePlanVersion = () => {
    planConfigService.getDpPlanConfig()
    .then((response) => {
      if (response?.data?.data) {
        const configObj = response?.data?.data?.planConfigObj;
        setPlanConfigObj(configObj);
        setTimeDimension(configObj?.BUCKET_UNIT);
      }
    }).catch(e => {
      debug(e);
    })
    .finally(() => {
      setLoading(true);
    });
};

  const retrieveBfAccuracy = () => {
    onBtShowLoading();
    const params = getRequestParams();
    bfAccuracyService.getBfAccuracy(params)
      .then(response => {
        if (response?.data?.message) {
          const { message } = response.data;
          setGridData([]);
          onBtHide();
          setSnackInfo({ open: true, severity: message.type, content: t(message.message)});
          return;
        }
        if (!isEmpty(response.data)) {
          handleLoadData(response.data);
        } else {
          setGridData([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        debug(e);
        onBtHide();
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
      })
      .then(() => {
        onBtHide();
        const reduxState = reduxUtil.createObjectState(viewName, { currentPage, pageSize, startDate, endDate, timeDimension, selectVersion, 
          filter: filterValue, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
      })
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBfAccuracy();
    }
  };

  const onChangePeriod = (newValue, target) => {
    if (newValue) {
      if (isEqual(target, PERIOD_START)) {
        setStartDate(newValue)
      } else if (isEqual(target, PERIOD_END)) {
        setEndDate(newValue)
      }
    }
  };

  useEffect(() => {
    retrievePlanVersion();
    retrieveMeasureDisplayName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // posActualFlg는 버전마다 값이 다르므로(선택된 plan version 기준), selectVersion이 실제로 정해진
  // 뒤에만 allMeasure를 계산한다. allMeasureSetRef로 딱 한 번만 세팅해 이후 버전 변경에는 반응하지 않는다.
  useEffect(() => {
    if (!allMeasureSetRef.current && selectVersion?.id) {
      allMeasureSetRef.current = true;
      const qtyName = selectVersion?.posActualFlg ? POS_SA_QTY.name : SA_QTY.name;
      const nextAllMeasure = [qtyName, BF_QTY.name, GAP_QTY, ACCURACY];
      setActualQtyName(qtyName);
      setAllMeasure(nextAllMeasure);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion?.id]);

  useEffect(() => {
    if (loading) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, loading]);

  useEffect(() => {
    if (loading && dateLoad && !isEmpty(measures)) {
      retrieveBfAccuracy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (loading && dateLoad && !isEmpty(measures)) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, measures]);

  useEffect(() => {
    if (reduxViewState?.timeDimension && !isEqual(reduxViewState?.timeDimension, timeDimension)) {
      setStartDate(dateUtils.nearDay(startDate, planConfigObj?.START_DAY || codeUtil.dayOfWeek.MONDAY.cd, timeDimension));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeDimension]);

  useEffect(() => {
    if (loading) {
      let sDate = '';
      let eDate = '';
      if (!isEmpty(selectVersion) && !isUndefined(selectVersion)) {
        sDate = new ZDate(selectVersion.startDate || startDate);
        eDate = dateUtils.calculateRangeDate(sDate, selectVersion.bucket, selectVersion.startDay, selectVersion.planningRange, true);
        setMaxDate(eDate);
      } else {
        sDate = startDate;
        eDate = endDate;
        setMaxDate(MAX_END_DATE);
      }
      if(!reload && reduxViewState?.startDate && reduxViewState?.endDate ) {
        setStartDate(reduxViewState?.startDate);
        setEndDate(reduxViewState?.endDate);
      } else {
        setStartDate(dateUtils.nearDay(sDate, planConfigObj?.START_DAY || codeUtil.dayOfWeek.MONDAY.cd, planConfigObj?.BUCKET_UNIT || timeDimension));
        setEndDate(eDate);
      }
      setReload(true);
      setDateLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion?.id, loading]);
  
  useEffect(() => {
    if (loading) {
      if (filterValue?.customerHrchys || filterValue?.category) {
        if (filterValue?.customerHrchys) customerSearch.current = "";
        if (filterValue?.category) itemSearch.current = "";
        initPageToRetrieve();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue?.customerHrchys, filterValue?.category]);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData, viewName]);

  return (
    <>
      <FilterContainer>
        <DpVersionSelect
          selectedVersion={setSelectVersion}
          reduxVersion={reduxViewState?.selectVersion ?? null}
          reload={vListReload}
          setReload={setVListReload}
          status={[codeUtil.dpVersion.RELEASED]}
          isMain
          disableClearable={false}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t(PERIOD_START)}
            views={codeUtil.isWeekBucket(timeDimension) ? ["year", "month", "day"] : ["year", "month"]}
            format={codeUtil.isWeekBucket(timeDimension) ? "yyyy-MM-dd" : "yyyy-MM"}
            maxDate={endDate}
            value={startDate}
            openTo={"month"}
            shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, timeDimension, planConfigObj?.START_DAY || codeUtil.dayOfWeek.MONDAY.cd )}
            onAccept={newValue => {
              onChangePeriod(newValue, PERIOD_START);
            }}
            slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
          />
          <DatePicker
            label={t(PERIOD_END)}
            views={codeUtil.isWeekBucket(timeDimension) ? ["year", "month", "day"] : ["year", "month"]}
            format={codeUtil.isWeekBucket(timeDimension) ? "yyyy-MM-dd" : "yyyy-MM"}
            minDate={startDate}
            maxDate={maxDate}
            value={endDate}
            openTo={"month"}
            onAccept={newValue => {
              onChangePeriod(newValue, PERIOD_END);
            }}
            slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
          />
        </LocalizationProvider>
          <CustomerHrchyTreeFilter
            viewName={viewName}
            defaultValue={custHrchyDefaultValue}
            enableSelectParent
            enableMultiSelect
            onChangeFilter={setFilterValue}
            styles={{ width: 290 }}
          />
          <CustomerAutocomplete
            setSelectedCustomer={initPageToRetrieve}
            searchValue={customerSearch}
            searchFields={ElasticSearchUtil.defaultCustomerSearchFields}
            sx={{ display: "inline-block", width: 240, mr: 1, }}
            extraCodesRef={extraCustomerCodesRef}
          />
          <ItemHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={itemHrchyDefaultValue}
            enableMultiSelect
            enableSelectParent
            styles={{ width: 290 }}
          />
          <DPItemAutocomplete
            setSelectedItem={initPageToRetrieve}
            searchValue={itemSearch}
            searchFields={ElasticSearchUtil.defaultItemSearchFields}
            sx={{ display: "inline-block", width: 240 }}
            extraCodesRef={extraItemCodesRef}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton
            title={title}
            service={bfAccuracyService}
            columnDefs={exportColumnDefs.current}
            setExcelData={setGridDatas}
            formatHeaderList={formatHeaders}
            params={{ ...getRequestParams() }}
            dataType={"data"}
            isCsv
            translationColumns={["measure"]}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1.2]} collapseDirection='up'>
        <Box className="chart-area">
          <ChartComposed
            xAxis={["yyyymmdd"]}
            data={chartData}
            dataSeries={chartSeries}
            tooltipAnimation={false}
            options={{ minHeight: "200px" }}
            activeMeasures={getDisplayName(measures)}
          />
        </Box>
        <Box className="ag-theme-balham chart-grid-area" sx={{ height: "calc(100% - 50px)", mt: 2 }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridData}
            onGridReady={onGridReady}
            {...defaultGridMemo}
            rowSelection={{
              ...defaultGridMemo.rowSelection,
              groupSelects: "descendants",
            }}
            onCellClicked={onCellGridClicked}
            getRowStyle={getRowStyle}
            suppressAutoSize
            onRowSelected={onRowSelected}
            groupHideOpenParents
            animateRows
            statusBar={statusBar}
            groupDisplayType={"custom"}
            onComponentStateChanged={initCheck}
            groupDefaultExpanded={-1}
            onSortChanged={params => settingSortParams(params)}
            // onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
          />
          <PaginationContainer
            currentPage={currentPage} totalPages={gridPages}
            pageSize={pageSize} pageSizes={pageSizes}
            setCurrentPage={setCurrentPage} setPageSize={setPageSize}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {allMeasure && (
        <MeasureModal
          open={measureModalOpen}
          setOpen={setMeasureModalOpen}
          viewName={viewName}
          allMeasure={allMeasure}
          applyMeasures={applyMeasures}
          displayNames={displayNames}
          hasDisplayName
        />
      )}
    </>
  );
};

export default withTranslation()(BfAccuracyReport);
